import {
  ANALYSIS_MESSAGES,
  ONBOARDING_ANALYZING_MESSAGE_INTERVAL_MS,
  ONBOARDING_ANALYZING_MIN_MESSAGES,
  STEP_COPY,
} from '../constants';
import { useOnboardingAnalysisSettle } from '../hooks/useOnboardingAnalysisSettle';
import type { InvestingStyleId } from '../types';
import AnimatedInsightNumber from './AnimatedInsightNumber';
import { highlightedNumberClassName } from './InsightPointer';
import InsightSummary from './InsightSummary';
import OnboardingAnalyzingState from './OnboardingAnalyzingState';
import OnboardingInsightPanelShell from './OnboardingInsightPanelShell';

interface InvestingStyleInsightProps {
  styleIds: InvestingStyleId[];
  matchCount: number;
  isCountLoading: boolean;
  hasCountError: boolean;
  /** When true, show the summary immediately on first mount only (once counts are ready). */
  skipInitialAnimation?: boolean;
}

// Long enough for the minimum number of analyzing messages to each be readable.
const INVESTING_STYLE_SETTLE_MS =
  ONBOARDING_ANALYZING_MESSAGE_INTERVAL_MS * ONBOARDING_ANALYZING_MIN_MESSAGES;

export default function InvestingStyleInsight({
  styleIds,
  matchCount,
  isCountLoading,
  hasCountError,
  skipInitialAnimation = false,
}: InvestingStyleInsightProps) {
  const selectionKey = styleIds.join(',');
  const isSettled = useOnboardingAnalysisSettle(selectionKey, {
    skipInitialAnimation,
    settleMs: INVESTING_STYLE_SETTLE_MS,
  });

  const isAnalyzing = styleIds.length > 0 && !isSettled;
  const displayCount = hasCountError && !isCountLoading ? 0 : matchCount;

  if (styleIds.length === 0) {
    return null;
  }

  return (
    <OnboardingInsightPanelShell isAnalyzing={isAnalyzing}>
      <div className="grid w-full" role="status" aria-live="polite">
        <div
          className={`col-start-1 row-start-1 ${isAnalyzing ? '' : 'hidden'}`}
          aria-hidden={!isAnalyzing}
        >
          <OnboardingAnalyzingState
            selectionKey={selectionKey}
            messages={ANALYSIS_MESSAGES.investingStyle}
            settleMs={INVESTING_STYLE_SETTLE_MS}
            messageIntervalMs={ONBOARDING_ANALYZING_MESSAGE_INTERVAL_MS}
            isActive={isAnalyzing}
          />
        </div>
        <div
          className={`col-start-1 row-start-1 ${isAnalyzing ? 'hidden' : ''}`}
          aria-hidden={isAnalyzing}
        >
          <InsightSummary
            ariaLabel="Investing style analysis summary"
            heading={
              <>
                Found{' '}
                <AnimatedInsightNumber
                  value={displayCount}
                  className={highlightedNumberClassName}
                />{' '}
                {STEP_COPY.investingStyle.matchResultLabel}
              </>
            }
          />
        </div>
      </div>
    </OnboardingInsightPanelShell>
  );
}
